import { ActionIcon, Tooltip } from '@mantine/core';
import { Environment, type CameraControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { LoadingAnimation, variants } from '@uag/react-core';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxHelper, type Group } from 'three';
import { useShallow } from 'zustand/shallow';

import { PreviewCameraControls } from 'app/modules/wallforming/components/scenes/camera-controls/PreviewCameraControls';
import { PreviewOrientationCube } from 'app/modules/wallforming/components/scenes/camera-controls/PreviewOrientationCube';
import { setFocusOnSolution } from 'app/modules/wallforming/components/scenes/camera-controls/utils/cameraUtils';
import { PlacementPreview } from 'app/modules/wallforming/components/scenes/objects/placement-preview/PlacementPreview';
import { SceneLighting } from 'app/modules/wallforming/components/scenes/SceneLighting';
import { ScenePostProcessing } from 'app/modules/wallforming/components/scenes/ScenePostProcessing';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const screenWidth = window.screen.width;
const TIMEOUT_DELAY_MS = 300;

export const PlacementSolutionPreview = () => {
    const { t } = useTranslation();

    const previewCameraControlsRef = useRef<CameraControls | null>(null);
    const previewHelperRef = useRef<BoxHelper | undefined>(undefined);
    const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);

    const { selectedSolution, automatedPlacementTab } = usePlacementStore(
        useShallow((state) => {
            const index = state.selectedPlacementSolution;
            return {
                selectedSolution: index === null ? null : (state.placementSolutions[index] ?? null),
                automatedPlacementTab: state.automatedPlacementTab
            };
        })
    );

    const { shadowsEnabled, reflectionsEnabled } = usePlacementStore(useShallow((state) => state.graphicsSettings));

    const wheelClickCountRef = useRef<number>(0);
    const wheelClickTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    const handleFocusSolution = useCallback(() => {
        setFocusOnSolution(previewCameraControlsRef.current, previewHelperRef.current, selectedSolution);
    }, [selectedSolution]);

    const handleOnSetFocus = useCallback(
        (group: Group) => {
            previewHelperRef.current?.geometry.dispose();
            previewHelperRef.current = new BoxHelper(group, 0xffff00);
            handleFocusSolution();
        },
        [handleFocusSolution]
    );

    const handleOnPointerDown = (pointerEvent: React.PointerEvent): void => {
        if (pointerEvent.button === 1) {
            wheelClickCountRef.current++;

            if (wheelClickCountRef.current === 2) {
                handleFocusSolution();
                clearTimeout(wheelClickTimerRef.current);
                wheelClickCountRef.current = 0;
            } else {
                wheelClickTimerRef.current = setTimeout(() => {
                    wheelClickCountRef.current = 0;
                }, TIMEOUT_DELAY_MS);
            }
        }

        pointerEvent.stopPropagation();
    };

    return (
        <div
            className="relative h-full w-full overflow-hidden border border-border-ultra-light"
            onPointerDown={handleOnPointerDown}
        >
            <div className="relative">
                <div className="absolute top-3 left-3 z-50">
                    <Tooltip label={t('buttonFocusSolution')} position="right" variant={variants.tooltip.right}>
                        <ActionIcon
                            aria-label={t('buttonFocusSolution')}
                            className={`
                                bg-background-gray-ultra-light hover:bg-background-gray! catalogue-preview-action-icon
                                ${screenWidth >= 1920 ? 'action-icon-large' : 'action-icon-medium'}
                            `}
                            onClick={handleFocusSolution}
                        >
                            view_in_ar
                        </ActionIcon>
                    </Tooltip>
                </div>
            </div>
            {isLoadingPreview && automatedPlacementTab === PlacementTab.SOLUTIONS && (
                <div className="absolute inset-0 z-40 flex items-center justify-center bg-background-default/80">
                    <LoadingAnimation text={t('labelLoadSolution')} />
                </div>
            )}

            <Canvas frameloop="demand" shadows={shadowsEnabled ? 'soft' : false} orthographic>
                <PreviewCameraControls previewCameraControlsRef={previewCameraControlsRef} />
                <PreviewOrientationCube />
                <PlacementPreview
                    isMainCanvas={false}
                    centered
                    onBoundsReady={handleOnSetFocus}
                    onLoadingChange={setIsLoadingPreview}
                />
                <Environment
                    background={false}
                    environmentIntensity={shadowsEnabled || reflectionsEnabled ? 1.6 : 0}
                    environmentRotation={[Math.PI / 2, -1, 0]}
                    preset="city"
                />
                <SceneLighting />
                <ScenePostProcessing />
            </Canvas>
        </div>
    );
};
